// =============================================================================
// forecastBankHolidays.js — the live engine's BANK-HOLIDAY calendar, rebuilt on our side.
// Lane BQ, Run #14, 13 Sep 2026. PURE, network-free.
//
// WHAT THE LIVE ENGINE DOES (ManchesterForecastEngine.js isBankHoliday, read 13 Sep 2026):
//   * fetches https://www.gov.uk/bank-holidays.json, takes
//       data['england-and-wales'].events.map(e => e.date)
//     — EVERY year the file holds (2019 … 2027 today), nothing else — and caches that
//     list for 24 h;
//   * a non-200 answer → "not a bank holiday" (no flag, silently);
//   * the list is consulted in TWO places: the TARGET day (+0.20, the "Bank Holiday"
//     chip) and every history / previous-year day it averages, where a bank holiday is
//     divided back out (getHistoricalContextMultiplier) — the day is KEPT in the
//     window, not excluded.
//
// Here the list lives in migration 1320 (forecast_bank_holidays), fed by
// tools/lane_bq_bank_holidays_import.mjs from the same gov.uk file, and read by the
// kiosk through forecast_bank_holidays_feed. The dates ride the engine config
// (forecast.js DEFAULT_CONFIG.bankHolidays) so the SAME list reaches both of the
// engine's consulting points without any special-casing.
// =============================================================================

export const GOV_UK_BANK_HOLIDAYS_URL = "https://www.gov.uk/bank-holidays.json";
export const REGION = "england-and-wales";

const isIsoDay = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || ""));

// gov.uk JSON → [{ day, title, notes }] for ONE region (the engine's), or null when
// the body is not the gov.uk shape (the engine would throw there and add no flag).
export function parseGovUkBankHolidays(json, region = REGION) {
  if (!json || typeof json !== "object") return null;
  const div = json[region];
  if (!div || !Array.isArray(div.events)) return null;
  const rows = [];
  for (const e of div.events) {
    if (!e || !isIsoDay(e.date)) continue;
    rows.push({ day: e.date, title: String(e.title || ""), notes: String(e.notes || "") });
  }
  return rows;
}

// Rows → sorted, de-duplicated ISO dates (the shape the engine keeps in its cache).
export function bankHolidayDates(rows) {
  const set = new Set();
  for (const r of rows || []) {
    const d = typeof r === "string" ? r : r && r.day;
    if (isIsoDay(d)) set.add(d);
  }
  return Array.from(set).sort();
}

// The 1320 feed's answer → ISO dates. Null / error / odd shapes → [] (no flag), which
// is exactly the engine's behaviour when gov.uk is unreachable.
export function holidaysFromFeed(feed) {
  if (!feed || typeof feed !== "object" || feed.error || !Array.isArray(feed.holidays)) return [];
  return bankHolidayDates(feed.holidays);
}

const shiftIso = (iso, days) =>
  new Date(Date.parse(String(iso).slice(0, 10) + "T12:00:00Z") + days * 86400000).toISOString().slice(0, 10);

// The window the reader loads once per calendar day: wide enough that any target the
// kitchen can pick (today/tomorrow, a few days either side) has its six-week history
// (−42 d), its previous-year 3-day window (−372 … −358 d) and its own date covered.
export function bankHolidayWindow(todayIso, spanDays = 450) {
  return { from: shiftIso(todayIso, -spanDays), to: shiftIso(todayIso, spanDays) };
}
